import type { ReusableDocument } from '@/types'

const INVALID_FILE_NAME_CHARACTERS = new Set([
  '/',
  '\\',
  ':',
  '*',
  '?',
  '"',
  '<',
  '>',
  '|',
])

function hasInvalidFileNameCharacter(value: string) {
  return Array.from(value).some(character => {
    const codePoint = character.codePointAt(0) ?? 0
    return (
      INVALID_FILE_NAME_CHARACTERS.has(character) ||
      codePoint < 32 ||
      codePoint === 127
    )
  })
}

export type DocumentRenameValidation = 'required' | 'invalid' | 'tooLong' | null

export function fileNameExtension(fileName: string) {
  const match = fileName.match(/\.[^.]+$/)
  return match?.[0] ?? ''
}

export function fileNameBase(fileName: string) {
  const extension = fileNameExtension(fileName)
  return extension ? fileName.slice(0, -extension.length) : fileName
}

export function renameLocalFile(file: File, baseName: string) {
  return new File([file], `${baseName}${fileNameExtension(file.name)}`, {
    type: file.type,
    lastModified: file.lastModified,
  })
}

export function documentExtension(doc: ReusableDocument) {
  const source = doc.file_name || doc.name
  return fileNameExtension(source)
}

export function documentBaseName(doc: ReusableDocument) {
  const extension = documentExtension(doc)
  if (!extension) return doc.name
  return doc.name.toLowerCase().endsWith(extension.toLowerCase())
    ? fileNameBase(doc.name)
    : doc.name
}

export function validateDocumentRename(
  value: string,
  extension: string,
  maxLength = 150
): DocumentRenameValidation {
  const trimmed = value.trim()
  if (!trimmed) return 'required'
  if (
    hasInvalidFileNameCharacter(trimmed) ||
    trimmed === '.' ||
    trimmed === '..' ||
    /[. ]$/u.test(trimmed)
  )
    return 'invalid'
  if (Array.from(trimmed + extension).length > maxLength) return 'tooLong'
  return null
}
